import { useState, useRef, useEffect } from 'react';
import { 
  Upload, Circle, Square, Scissors, ShoppingCart, Info, X, Check, 
  ChevronRight, Shield, Zap, Lock, Star, FileImage, Link2, Link2Off,
  Printer, Layers
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';

// Price tiers for Plot Folienaufkleber
const plotPriceTiers = [
  { minQty: 1, price: 8.00 },
  { minQty: 2, price: 7.00 },
  { minQty: 3, price: 6.00 },
  { minQty: 5, price: 5.00 },
  { minQty: 10, price: 4.00 },
  { minQty: 25, price: 3.45 },
  { minQty: 50, price: 2.85 },
  { minQty: 100, price: 2.15 },
  { minQty: 250, price: 1.85 },
  { minQty: 500, price: 1.52 }
];

// Price tiers for Druck-Sticker
const druckPriceTiers = [
  { minQty: 1, price: 7.75 },
  { minQty: 2, price: 6.75 },
  { minQty: 3, price: 5.75 },
  { minQty: 5, price: 4.75 },
  { minQty: 10, price: 3.75 },
  { minQty: 25, price: 2.40 },
  { minQty: 50, price: 2.20 },
  { minQty: 100, price: 1.95 },
  { minQty: 250, price: 1.68 },
  { minQty: 500, price: 1.38 }
];

const MINIMUM_ORDER = 11.99;
const MAX_WIDTH = 55;

const druckShapes = [
  { id: 'square', label: 'Eckig' },
  { id: 'round', label: 'Rund' },
  { id: 'contour', label: 'Konturgenau freigestellt', extra: true }
];

const druckFinishes = [
  { id: 'glanz', label: 'Standard Glanz' },
  { id: 'matt', label: 'Standard Matt' },
  { id: 'laminat', label: 'Premium Schutzlaminat', extra: true }
];

const trustBadges = [
  { icon: Shield, text: 'Premium Qualität' },
  { icon: Zap, text: 'Schneller Versand' },
  { icon: Lock, text: 'Sichere Bestellung' }
];

// Helper function to get price per piece
const getPricePerPiece = (qty, tiers) => {
  let price = tiers[0].price;
  for (const tier of tiers) {
    if (qty >= tier.minQty) {
      price = tier.price;
    }
  }
  return price;
};

export const StickerShop = () => {
  const [activeTab, setActiveTab] = useState('plot');
  
  // Plot product state
  const [plotFile, setPlotFile] = useState(null);
  const [plotWidth, setPlotWidth] = useState(10);
  const [plotHeight, setPlotHeight] = useState(10);
  const [plotProportional, setPlotProportional] = useState(true);
  const [plotQty, setPlotQty] = useState(1);
  const [plotAspectRatio, setPlotAspectRatio] = useState(1);
  
  // Druck product state
  const [druckFile, setDruckFile] = useState(null);
  const [druckWidth, setDruckWidth] = useState(10);
  const [druckHeight, setDruckHeight] = useState(10);
  const [druckProportional, setDruckProportional] = useState(true);
  const [druckQty, setDruckQty] = useState(1);
  const [druckShape, setDruckShape] = useState('square');
  const [druckFinish, setDruckFinish] = useState('glanz');
  const [druckAspectRatio, setDruckAspectRatio] = useState(1);
  
  // Cart state
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  const plotFileRef = useRef(null);
  const druckFileRef = useRef(null);

  // Checkout form
  const [checkoutForm, setCheckoutForm] = useState({
    name: '', firma: '', email: '', telefon: '',
    strasse: '', plz: '', ort: '', hinweis: '', zahlungsart: 'Überweisung'
  });

  // Calculate prices
  const plotPricePerPiece = getPricePerPiece(plotQty, plotPriceTiers);
  const plotSubtotal = plotPricePerPiece * plotQty;
  const plotTotal = Math.max(plotSubtotal, MINIMUM_ORDER);
  const plotNeedsMinimum = plotSubtotal < MINIMUM_ORDER;

  const druckPricePerPiece = getPricePerPiece(druckQty, druckPriceTiers);
  const druckSubtotal = druckPricePerPiece * druckQty;
  const druckTotal = Math.max(druckSubtotal, MINIMUM_ORDER);
  const druckNeedsMinimum = druckSubtotal < MINIMUM_ORDER;

  // Handle proportional sizing for Plot
  const handlePlotWidthChange = (newWidth) => {
    const w = Math.min(parseFloat(newWidth) || 0, MAX_WIDTH);
    setPlotWidth(w);
    if (plotProportional && plotAspectRatio) {
      setPlotHeight(Math.round(w / plotAspectRatio * 10) / 10);
    }
  };

  const handlePlotHeightChange = (newHeight) => {
    const h = parseFloat(newHeight) || 0;
    setPlotHeight(h);
    if (plotProportional && plotAspectRatio) {
      const newW = Math.min(h * plotAspectRatio, MAX_WIDTH);
      setPlotWidth(Math.round(newW * 10) / 10);
    }
  };

  // Handle proportional sizing for Druck
  const handleDruckWidthChange = (newWidth) => {
    const w = Math.min(parseFloat(newWidth) || 0, MAX_WIDTH);
    setDruckWidth(w);
    if (druckProportional && druckAspectRatio) {
      setDruckHeight(Math.round(w / druckAspectRatio * 10) / 10);
    }
  };

  const handleDruckHeightChange = (newHeight) => {
    const h = parseFloat(newHeight) || 0;
    setDruckHeight(h);
    if (druckProportional && druckAspectRatio) {
      const newW = Math.min(h * druckAspectRatio, MAX_WIDTH);
      setDruckWidth(Math.round(newW * 10) / 10);
    }
  };

  // File handlers
  const handlePlotFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPlotFile({
        name: file.name,
        size: (file.size / 1024).toFixed(1) + ' KB',
        preview: URL.createObjectURL(file)
      });
      setPlotAspectRatio(1);
    }
  };

  const handleDruckFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDruckFile({
        name: file.name,
        size: (file.size / 1024).toFixed(1) + ' KB',
        preview: URL.createObjectURL(file)
      });
      setDruckAspectRatio(1);
    }
  };

  // Add to cart
  const addPlotToCart = () => {
    if (!plotFile) {
      alert('Bitte laden Sie zuerst eine Datei hoch.');
      return;
    }
    const item = {
      id: Date.now(),
      type: 'plot',
      name: 'Premium Plot Folienaufkleber',
      file: plotFile.name,
      width: plotWidth,
      height: plotHeight,
      quantity: plotQty,
      pricePerPiece: plotPricePerPiece,
      total: plotTotal
    };
    setCartItems([...cartItems, item]);
    setIsCartOpen(true);
  };

  const addDruckToCart = () => {
    if (!druckFile) {
      alert('Bitte laden Sie zuerst eine Datei hoch.');
      return;
    }
    const item = {
      id: Date.now(),
      type: 'druck',
      name: 'Premium Druck-Sticker',
      file: druckFile.name,
      width: druckWidth,
      height: druckHeight,
      quantity: druckQty,
      shape: druckShapes.find(s => s.id === druckShape)?.label,
      finish: druckFinishes.find(f => f.id === druckFinish)?.label,
      pricePerPiece: druckPricePerPiece,
      total: druckTotal
    };
    setCartItems([...cartItems, item]);
    setIsCartOpen(true);
  };

  const removeFromCart = (id) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + item.total, 0);

  const validateForm = () => {
    const errors = {};
    if (!checkoutForm.name.trim()) errors.name = 'Name ist erforderlich';
    if (!checkoutForm.email.trim()) errors.email = 'E-Mail ist erforderlich';
    else if (!/\S+@\S+\.\S+/.test(checkoutForm.email)) errors.email = 'Ungültige E-Mail-Adresse';
    if (!checkoutForm.strasse.trim()) errors.strasse = 'Straße ist erforderlich';
    if (!checkoutForm.plz.trim()) errors.plz = 'PLZ ist erforderlich';
    if (!checkoutForm.ort.trim()) errors.ort = 'Ort ist erforderlich';
    return errors;
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    
    console.log('========================================');
    console.log('CHECKOUT SUBMIT HANDLER TRIGGERED');
    console.log('========================================');
    console.log('Timestamp:', new Date().toISOString());
    console.log('Form data:', JSON.stringify(checkoutForm, null, 2));
    console.log('Cart items:', JSON.stringify(cartItems, null, 2));
    console.log('Cart total:', cartTotal);
    
    // Validate form
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      console.log('VALIDATION FAILED:', errors);
      setSubmitError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }
    
    console.log('Validation passed, setting isSubmitting=true');
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Use current origin if REACT_APP_BACKEND_URL is not set or empty
      const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      console.log('API_URL:', API_URL);
      console.log('window.location.origin:', window.location.origin);
      console.log('Full endpoint:', `${API_URL}/api/order`);
      
      const orderData = {
        name: checkoutForm.name,
        firma: checkoutForm.firma,
        email: checkoutForm.email,
        telefon: checkoutForm.telefon,
        strasse: checkoutForm.strasse,
        plz: checkoutForm.plz,
        ort: checkoutForm.ort,
        hinweis: checkoutForm.hinweis,
        zahlungsart: checkoutForm.zahlungsart,
        items: cartItems,
        total: cartTotal
      };
      
      console.log('Order data prepared:', JSON.stringify(orderData, null, 2));
      console.log('SENDING FETCH REQUEST NOW...');
      
      const response = await fetch(`${API_URL}/api/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });
      
      console.log('FETCH RESPONSE RECEIVED');
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      const result = await response.json();
      console.log('Response data:', JSON.stringify(result, null, 2));
      
      if (response.ok && result.success) {
        console.log('ORDER SUCCESS! Order ID:', result.order_id);
        setOrderId(result.order_id);
        setOrderComplete(true);
      } else {
        console.log('ORDER FAILED:', result);
        setSubmitError(result.detail || 'Fehler beim Aufgeben der Bestellung');
      }
    } catch (error) {
      console.error('========================================');
      console.error('FETCH ERROR CAUGHT');
      console.error('========================================');
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setSubmitError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
    } finally {
      console.log('Setting isSubmitting=false');
      setIsSubmitting(false);
    }
  };

  const resetShop = () => {
    setCartItems([]);
    setPlotFile(null);
    setDruckFile(null);
    setShowCheckout(false);
    setOrderComplete(false);
    setOrderId('');
    setSubmitError('');
    setFormErrors({});
    setCheckoutForm({
      name: '', firma: '', email: '', telefon: '',
      strasse: '', plz: '', ort: '', hinweis: '', zahlungsart: 'Überweisung'
    });
  };

  // Order Complete View
  if (orderComplete) {
    return (
      <section id="sticker" className="py-24 lg:py-32 bg-slate-50" data-testid="sticker-shop-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl border border-slate-100">
            <div className="w-24 h-24 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="w-12 h-12 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Danke für Ihre Bestellung!</h2>
            {orderId && (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-teal-700">Ihre Bestellnummer:</p>
                <p className="text-2xl font-bold text-teal-600 font-mono">{orderId}</p>
              </div>
            )}
            <p className="text-lg text-slate-600 mb-8">
              Ihre Bestellung wurde erfolgreich aufgenommen. Sie erhalten in Kürze eine Bestätigung per E-Mail an <strong>{checkoutForm.email}</strong>.
            </p>
            <Button onClick={resetShop} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-10 py-6 text-lg font-semibold" data-testid="order-complete-reset">
              Neue Bestellung
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // Checkout View
  if (showCheckout) {
    return (
      <section id="sticker" className="py-24 lg:py-32 bg-slate-50" data-testid="sticker-shop-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
                <div className="bg-slate-900 px-8 py-6">
                  <h2 className="text-2xl font-bold text-white">Checkout</h2>
                </div>
                <form onSubmit={handleCheckoutSubmit} className="p-8 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700 font-medium">Name *</Label>
                      <Input 
                        required 
                        value={checkoutForm.name} 
                        onChange={(e) => { setCheckoutForm({...checkoutForm, name: e.target.value}); setFormErrors({...formErrors, name: ''}); }} 
                        className={`mt-2 ${formErrors.name ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder="Ihr vollständiger Name"
                      />
                      {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Firma (optional)</Label>
                      <Input value={checkoutForm.firma} onChange={(e) => setCheckoutForm({...checkoutForm, firma: e.target.value})} className="mt-2" placeholder="Firmenname" />
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">E-Mail *</Label>
                      <Input 
                        type="email" 
                        required 
                        value={checkoutForm.email} 
                        onChange={(e) => { setCheckoutForm({...checkoutForm, email: e.target.value}); setFormErrors({...formErrors, email: ''}); }} 
                        className={`mt-2 ${formErrors.email ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder="ihre@email.de"
                      />
                      {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Telefon (optional)</Label>
                      <Input value={checkoutForm.telefon} onChange={(e) => setCheckoutForm({...checkoutForm, telefon: e.target.value})} className="mt-2" placeholder="+49 ..." />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-slate-700 font-medium">Straße & Hausnummer *</Label>
                      <Input 
                        required 
                        value={checkoutForm.strasse} 
                        onChange={(e) => { setCheckoutForm({...checkoutForm, strasse: e.target.value}); setFormErrors({...formErrors, strasse: ''}); }} 
                        className={`mt-2 ${formErrors.strasse ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder="Musterstraße 123"
                      />
                      {formErrors.strasse && <p className="text-red-500 text-sm mt-1">{formErrors.strasse}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">PLZ *</Label>
                      <Input 
                        required 
                        value={checkoutForm.plz} 
                        onChange={(e) => { setCheckoutForm({...checkoutForm, plz: e.target.value}); setFormErrors({...formErrors, plz: ''}); }} 
                        className={`mt-2 ${formErrors.plz ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder="12345"
                      />
                      {formErrors.plz && <p className="text-red-500 text-sm mt-1">{formErrors.plz}</p>}
                    </div>
                    <div>
                      <Label className="text-slate-700 font-medium">Ort *</Label>
                      <Input 
                        required 
                        value={checkoutForm.ort} 
                        onChange={(e) => { setCheckoutForm({...checkoutForm, ort: e.target.value}); setFormErrors({...formErrors, ort: ''}); }} 
                        className={`mt-2 ${formErrors.ort ? 'border-red-500 bg-red-50' : ''}`}
                        placeholder="Musterstadt"
                      />
                      {formErrors.ort && <p className="text-red-500 text-sm mt-1">{formErrors.ort}</p>}
                    </div>
                  </div>

                  {/* Payment Methods */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <h4 className="font-bold text-slate-900 mb-4">Zahlungsmethode auswählen *</h4>
                    <div className="space-y-4">
                      {/* PayPal */}
                      <label className={`block bg-white rounded-lg p-4 border-2 cursor-pointer transition-all ${checkoutForm.zahlungsart === 'PayPal' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="zahlungsart"
                            value="PayPal"
                            checked={checkoutForm.zahlungsart === 'PayPal'}
                            onChange={(e) => setCheckoutForm({...checkoutForm, zahlungsart: e.target.value})}
                            className="w-5 h-5 text-blue-600"
                          />
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">PayPal</p>
                            <p className="text-sm text-slate-500">akw-design@gmx.de</p>
                          </div>
                        </div>
                      </label>
                      
                      {/* Bank Transfer */}
                      <label className={`block bg-white rounded-lg p-4 border-2 cursor-pointer transition-all ${checkoutForm.zahlungsart === 'Überweisung' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <input
                            type="radio"
                            name="zahlungsart"
                            value="Überweisung"
                            checked={checkoutForm.zahlungsart === 'Überweisung'}
                            onChange={(e) => setCheckoutForm({...checkoutForm, zahlungsart: e.target.value})}
                            className="w-5 h-5 text-teal-600"
                          />
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                            <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          </div>
                          <p className="font-semibold text-slate-900">Überweisung</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1 ml-8">
                          <p><span className="text-slate-500">Empfänger:</span> <span className="font-medium text-slate-900">Adrian Wojciechowski</span></p>
                          <p><span className="text-slate-500">IBAN:</span> <span className="font-mono text-slate-900">DE43 1001 0178 3526 3057 81</span></p>
                          <p><span className="text-slate-500">Bank:</span> <span className="text-slate-900">Revolut Bank UAB</span></p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Hinweis */}
                  <div>
                    <Label className="text-slate-700 font-medium">Anmerkungen zur Bestellung (optional)</Label>
                    <textarea 
                      value={checkoutForm.hinweis} 
                      onChange={(e) => setCheckoutForm({...checkoutForm, hinweis: e.target.value})} 
                      className="mt-2 w-full min-h-[100px] rounded-lg border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="Besondere Wünsche oder Anmerkungen..."
                    />
                  </div>

                  {submitError && (
                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                      <p className="text-red-700 font-medium">{submitError}</p>
                    </div>
                  )}

                  <div className="flex gap-4">
                    <Button type="button" variant="outline" onClick={() => setShowCheckout(false)} className="flex-1 rounded-full py-6" disabled={isSubmitting}>
                      Zurück
                    </Button>
                    <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 font-semibold disabled:opacity-50" disabled={isSubmitting}>
                      {isSubmitting ? 'Wird gesendet...' : 'Bestellung absenden'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6 sticky top-24">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Bestellübersicht</h3>
                <div className="space-y-4 mb-6">
                  {cartItems.map((item) => (
                    <div key={item.id} className="pb-4 border-b border-slate-100">
                      <p className="font-semibold text-slate-900">{item.quantity}x {item.name}</p>
                      <p className="text-sm text-slate-500">{item.width} x {item.height} cm</p>
                      <p className="font-bold text-teal-600 mt-1">{item.total.toFixed(2)} €</p>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center pt-4 border-t-2 border-slate-200">
                  <p className="text-lg font-bold">Gesamt</p>
                  <p className="text-2xl font-bold text-teal-600">{cartTotal.toFixed(2)} €</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Main Shop View
  return (
    <section id="sticker" className="py-24 lg:py-32 bg-white" data-testid="sticker-shop-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 px-4 py-1.5 text-sm font-semibold mb-4">
            ONLINE SHOP
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Produkte konfigurieren
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Wähle dein Produkt, lade dein Design hoch und konfiguriere Größe & Menge.
          </p>
        </div>

        {/* Product Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-2 mb-12 h-14 p-1 bg-slate-100 rounded-full">
            <TabsTrigger 
              value="plot" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-md text-base font-semibold"
              data-testid="tab-plot"
            >
              <Layers className="w-5 h-5 mr-2" />
              Plot Folienaufkleber
            </TabsTrigger>
            <TabsTrigger 
              value="druck" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-md text-base font-semibold"
              data-testid="tab-druck"
            >
              <Printer className="w-5 h-5 mr-2" />
              Druck-Sticker
            </TabsTrigger>
          </TabsList>

          {/* PLOT PRODUCT */}
          <TabsContent value="plot" className="mt-0">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Left - Info & Preview */}
              <div className="lg:col-span-5">
                <div className="sticky top-24 space-y-6">
                  {/* Product Info Card */}
                  <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                    <Badge className="bg-teal-600 text-white mb-4">Einfarbig geplottet</Badge>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Premium Plot Folienaufkleber</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      Wir produzieren hochwertige, konturgeschnittene Plot-Folien nach deinem individuellen Design. Ob Firmenlogo, Schriftzug oder spezielle Form – dein Motiv wird sauber geplottet und montagefertig vorbereitet.
                    </p>
                    <div className="space-y-2">
                      {['Premium gegossene Plot-Folie', 'Konturgenauer Präzisionsschnitt', 'Langlebig & stark haftend', 'Für Innen- und Außenanwendungen', 'Sauber entgittert & montagefertig'].map((benefit, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                          <Check className="w-4 h-4 text-teal-600" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-4">Vorschau</h4>
                    <div className="aspect-square bg-slate-50 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-200">
                      {plotFile ? (
                        <div className="text-center p-4">
                          {plotFile.preview && (
                            <img src={plotFile.preview} alt="Vorschau" className="max-w-32 max-h-32 mx-auto mb-3 object-contain" />
                          )}
                          <p className="text-sm font-medium text-slate-700">{plotFile.name}</p>
                          <p className="text-xs text-slate-500 mt-1">{plotWidth} x {plotHeight} cm</p>
                        </div>
                      ) : (
                        <div className="text-center text-slate-400">
                          <FileImage className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Vorschau erscheint hier</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Configurator */}
              <div className="lg:col-span-7 space-y-6">
                {/* File Upload */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-lg font-bold text-slate-900 mb-4 block">1. Datei hochladen</Label>
                  <input ref={plotFileRef} type="file" accept=".png,.pdf,.svg,.jpg,.jpeg" onChange={handlePlotFileUpload} className="hidden" />
                  <button
                    onClick={() => plotFileRef.current?.click()}
                    className={`w-full border-2 border-dashed rounded-xl p-6 transition-all ${
                      plotFile ? 'border-teal-300 bg-teal-50' : 'border-slate-200 hover:border-teal-400'
                    }`}
                    data-testid="plot-upload-button"
                  >
                    {plotFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-5 h-5 text-teal-600" />
                        <span className="font-medium text-teal-700">{plotFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                        <p className="text-slate-600 font-medium">PNG, PDF, SVG oder JPG</p>
                      </div>
                    )}
                  </button>
                </div>

                {/* Size */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-lg font-bold text-slate-900">2. Größe & Format</Label>
                    <div className="flex items-center gap-2">
                      {plotProportional ? <Link2 className="w-4 h-4 text-teal-600" /> : <Link2Off className="w-4 h-4 text-slate-400" />}
                      <span className="text-sm text-slate-600">Proportional</span>
                      <Switch checked={plotProportional} onCheckedChange={setPlotProportional} />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Max. Breite: {MAX_WIDTH} cm. Das Seitenverhältnis bleibt standardmäßig erhalten.</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Breite (cm)</Label>
                      <Input
                        type="number"
                        value={plotWidth}
                        onChange={(e) => handlePlotWidthChange(e.target.value)}
                        min="1"
                        max={MAX_WIDTH}
                        step="0.1"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Höhe (cm)</Label>
                      <Input
                        type="number"
                        value={plotHeight}
                        onChange={(e) => handlePlotHeightChange(e.target.value)}
                        min="1"
                        step="0.1"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {plotWidth > MAX_WIDTH && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800">
                        <Info className="w-4 h-4 inline mr-1" />
                        Die maximale Breite pro Element beträgt {MAX_WIDTH} cm. Größere Motive werden fachgerecht auf mehrere Bahnen aufgeteilt.
                      </p>
                    </div>
                  )}
                </div>

                {/* Quantity */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-lg font-bold text-slate-900 mb-4 block">3. Stückzahl</Label>
                  <p className="text-sm text-slate-500 mb-4">Der Preis richtet sich nach Größe und Stückzahl. Mit steigender Menge reduziert sich der Einzelpreis.</p>
                  <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" onClick={() => setPlotQty(Math.max(1, plotQty - 1))} className="w-12 h-12 rounded-xl">-</Button>
                    <Input
                      type="number"
                      value={plotQty}
                      onChange={(e) => setPlotQty(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      className="w-24 text-center text-xl font-bold"
                    />
                    <Button variant="outline" onClick={() => setPlotQty(plotQty + 1)} className="w-12 h-12 rounded-xl">+</Button>
                  </div>
                  
                  {/* Price Table */}
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Staffelpreise:</p>
                    <div className="grid grid-cols-5 gap-2 text-xs">
                      {plotPriceTiers.slice(0, 5).map((tier, i) => (
                        <div key={i} className={`p-2 rounded-lg text-center ${plotQty >= tier.minQty && (plotPriceTiers[i+1] ? plotQty < plotPriceTiers[i+1].minQty : true) ? 'bg-teal-100 text-teal-700' : 'bg-white'}`}>
                          <p className="font-semibold">ab {tier.minQty}</p>
                          <p>{tier.price.toFixed(2)} €</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-slate-900 rounded-2xl p-6 text-white">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-slate-400 text-sm">Stückzahl</p>
                      <p className="text-xl font-bold">{plotQty}x Plot-Aufkleber</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-sm">Pro Stück</p>
                      <p className="text-xl font-bold">{plotPricePerPiece.toFixed(2)} €</p>
                    </div>
                  </div>
                  
                  {plotNeedsMinimum && (
                    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-200">
                        Mindestbestellwert: {MINIMUM_ORDER.toFixed(2)} € (Subtotal: {plotSubtotal.toFixed(2)} €)
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-end pt-4 border-t border-slate-700">
                    <p className="text-lg font-semibold">Gesamt</p>
                    <p className="text-3xl font-bold text-teal-400">{plotTotal.toFixed(2)} €</p>
                  </div>
                  
                  <Button
                    onClick={addPlotToCart}
                    className="w-full mt-6 bg-teal-500 hover:bg-teal-400 text-slate-900 rounded-full py-6 text-lg font-bold"
                    data-testid="add-plot-to-cart"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    In den Warenkorb
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* DRUCK PRODUCT */}
          <TabsContent value="druck" className="mt-0">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
              {/* Left - Info & Preview */}
              <div className="lg:col-span-5">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                    <Badge className="bg-purple-600 text-white mb-4">Vollfarbig gedruckt</Badge>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">Premium Druck-Sticker</h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      Wir drucken dein Motiv in hochwertiger CMYK-Farbqualität auf strapazierfähiges Vinyl und schneiden es präzise entlang der Motivkontur aus. Farbverläufe, Details und feine Elemente werden sauber und brillant umgesetzt.
                    </p>
                    <div className="space-y-2">
                      {['Hochwertiger CMYK-Druck', 'Präziser Konturschnitt', 'Kräftige Farben & feine Details', 'Strapazierfähiges Vinyl', 'Ideal für Logos, Fotos & komplexe Designs'].map((benefit, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-slate-700">
                          <Check className="w-4 h-4 text-purple-600" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <h4 className="font-semibold text-slate-900 mb-4">Vorschau</h4>
                    <div className="aspect-square bg-slate-50 rounded-xl flex items-center justify-center border-2 border-dashed border-slate-200">
                      {druckFile ? (
                        <div className="text-center p-4">
                          {druckFile.preview && (
                            <img src={druckFile.preview} alt="Vorschau" className={`max-w-32 max-h-32 mx-auto mb-3 object-contain ${druckShape === 'round' ? 'rounded-full' : ''}`} />
                          )}
                          <p className="text-sm font-medium text-slate-700">{druckFile.name}</p>
                          <p className="text-xs text-slate-500 mt-1">{druckWidth} x {druckHeight} cm • {druckShapes.find(s => s.id === druckShape)?.label}</p>
                        </div>
                      ) : (
                        <div className="text-center text-slate-400">
                          <FileImage className="w-12 h-12 mx-auto mb-2 opacity-30" />
                          <p className="text-sm">Vorschau erscheint hier</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Configurator */}
              <div className="lg:col-span-7 space-y-6">
                {/* File Upload */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-lg font-bold text-slate-900 mb-4 block">1. Datei hochladen</Label>
                  <input ref={druckFileRef} type="file" accept=".png,.pdf,.svg,.jpg,.jpeg" onChange={handleDruckFileUpload} className="hidden" />
                  <button
                    onClick={() => druckFileRef.current?.click()}
                    className={`w-full border-2 border-dashed rounded-xl p-6 transition-all ${
                      druckFile ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:border-purple-400'
                    }`}
                    data-testid="druck-upload-button"
                  >
                    {druckFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Check className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-purple-700">{druckFile.name}</span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                        <p className="text-slate-600 font-medium">PNG, PDF, SVG oder JPG</p>
                      </div>
                    )}
                  </button>
                </div>

                {/* Shape & Finish */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <Label className="text-lg font-bold text-slate-900 mb-4 block">2. Form</Label>
                    <Select value={druckShape} onValueChange={setDruckShape}>
                      <SelectTrigger className="w-full h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {druckShapes.map((shape) => (
                          <SelectItem key={shape.id} value={shape.id}>
                            {shape.label} {shape.extra && <span className="text-purple-600">(+)</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <Label className="text-lg font-bold text-slate-900 mb-4 block">3. Oberfläche</Label>
                    <Select value={druckFinish} onValueChange={setDruckFinish}>
                      <SelectTrigger className="w-full h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {druckFinishes.map((finish) => (
                          <SelectItem key={finish.id} value={finish.id}>
                            {finish.label} {finish.extra && <span className="text-purple-600">(+)</span>}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Size */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <Label className="text-lg font-bold text-slate-900">4. Größe</Label>
                    <div className="flex items-center gap-2">
                      {druckProportional ? <Link2 className="w-4 h-4 text-purple-600" /> : <Link2Off className="w-4 h-4 text-slate-400" />}
                      <span className="text-sm text-slate-600">Proportional</span>
                      <Switch checked={druckProportional} onCheckedChange={setDruckProportional} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600 text-sm">Breite (cm)</Label>
                      <Input
                        type="number"
                        value={druckWidth}
                        onChange={(e) => handleDruckWidthChange(e.target.value)}
                        min="1"
                        max={MAX_WIDTH}
                        step="0.1"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Höhe (cm)</Label>
                      <Input
                        type="number"
                        value={druckHeight}
                        onChange={(e) => handleDruckHeightChange(e.target.value)}
                        min="1"
                        step="0.1"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200">
                  <Label className="text-lg font-bold text-slate-900 mb-4 block">5. Stückzahl</Label>
                  <div className="flex items-center gap-4 mb-6">
                    <Button variant="outline" onClick={() => setDruckQty(Math.max(1, druckQty - 1))} className="w-12 h-12 rounded-xl">-</Button>
                    <Input
                      type="number"
                      value={druckQty}
                      onChange={(e) => setDruckQty(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      className="w-24 text-center text-xl font-bold"
                    />
                    <Button variant="outline" onClick={() => setDruckQty(druckQty + 1)} className="w-12 h-12 rounded-xl">+</Button>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Staffelpreise:</p>
                    <div className="grid grid-cols-5 gap-2 text-xs">
                      {druckPriceTiers.slice(0, 5).map((tier, i) => (
                        <div key={i} className={`p-2 rounded-lg text-center ${druckQty >= tier.minQty && (druckPriceTiers[i+1] ? druckQty < druckPriceTiers[i+1].minQty : true) ? 'bg-purple-100 text-purple-700' : 'bg-white'}`}>
                          <p className="font-semibold">ab {tier.minQty}</p>
                          <p>{tier.price.toFixed(2)} €</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-slate-900 rounded-2xl p-6 text-white">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <p className="text-slate-400 text-sm">Stückzahl</p>
                      <p className="text-xl font-bold">{druckQty}x Druck-Sticker</p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-400 text-sm">Pro Stück</p>
                      <p className="text-xl font-bold">{druckPricePerPiece.toFixed(2)} €</p>
                    </div>
                  </div>
                  
                  {druckNeedsMinimum && (
                    <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4">
                      <p className="text-sm text-amber-200">
                        Mindestbestellwert: {MINIMUM_ORDER.toFixed(2)} €
                      </p>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-end pt-4 border-t border-slate-700">
                    <p className="text-lg font-semibold">Gesamt</p>
                    <p className="text-3xl font-bold text-purple-400">{druckTotal.toFixed(2)} €</p>
                  </div>
                  
                  <Button
                    onClick={addDruckToCart}
                    className="w-full mt-6 bg-purple-500 hover:bg-purple-400 text-white rounded-full py-6 text-lg font-bold"
                    data-testid="add-druck-to-cart"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    In den Warenkorb
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Clarity Notice */}
        <div className="mt-16 bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <div className="flex items-start gap-4">
            <Info className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-slate-900">Hinweis zur Umsetzung</p>
              <p className="text-slate-600 mt-1">
                Wir übernehmen Design und Produktion. Die Anbringung erfolgt durch Sie selbst oder einen Fachbetrieb Ihrer Wahl.
              </p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 flex flex-wrap justify-center gap-8">
          {trustBadges.map((badge, i) => (
            <div key={i} className="flex items-center gap-2 text-slate-600">
              <badge.icon className="w-5 h-5 text-teal-600" />
              <span className="font-medium">{badge.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Cart Sheet */}
      <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col" data-testid="cart-sheet">
          <SheetHeader className="border-b pb-4">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Warenkorb ({cartItems.length})
            </SheetTitle>
          </SheetHeader>
          
          <div className="flex-1 overflow-y-auto py-6">
            {cartItems.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p>Warenkorb ist leer</p>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="bg-slate-50 rounded-2xl p-5">
                    <div className="flex justify-between items-start">
                      <div>
                        <Badge className={item.type === 'plot' ? 'bg-teal-100 text-teal-700' : 'bg-purple-100 text-purple-700'}>
                          {item.type === 'plot' ? 'Plot' : 'Druck'}
                        </Badge>
                        <p className="font-bold text-slate-900 mt-2">{item.quantity}x {item.name}</p>
                        <p className="text-sm text-slate-500">{item.width} x {item.height} cm</p>
                        {item.shape && <p className="text-xs text-slate-400">{item.shape} • {item.finish}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900">{item.total.toFixed(2)} €</p>
                        <button onClick={() => removeFromCart(item.id)} className="text-sm text-red-500 hover:text-red-600 mt-2">
                          Entfernen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {cartItems.length > 0 && (
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-6">
                <span className="text-lg font-semibold">Gesamt</span>
                <span className="text-3xl font-bold text-teal-600">{cartTotal.toFixed(2)} €</span>
              </div>
              <Button
                onClick={() => { setIsCartOpen(false); setShowCheckout(true); }}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 text-lg font-semibold"
              >
                Zur Kasse
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </section>
  );
};
